import axios from 'axios';
import { markApiStart, markApiEnd } from '../utils/tabPerf';
import { API_BASE } from '../utils/apiBase';

const API_URL = API_BASE;

const _currentPath = () =>
  typeof window !== 'undefined' && window.location ? window.location.pathname : '';

/**
 * Translation table for FastAPI's English `detail` strings into user-facing
 * French messages. Applied in the response interceptor so every screen's
 * `err.response?.data?.detail` automatically renders in French — callers
 * don't have to change anything.
 */
const ERROR_DETAIL_FR = {
  'Not authenticated': 'Session expirée. Veuillez vous reconnecter.',
  'Could not validate credentials': 'Session invalide. Veuillez vous reconnecter.',
  'Invalid token type': 'Session invalide. Veuillez vous reconnecter.',
  'User account is deactivated': "Compte désactivé. Contactez l'administration.",
  'User not found': 'Utilisateur introuvable. Veuillez vous reconnecter.',
};

const translateErrorDetail = (error) => {
  // Network error (no response): browser, DNS, offline, CORS preflight, etc.
  if (!error.response) {
    error.frenchMessage = 'Connexion impossible. Vérifiez votre connexion internet.';
    return;
  }

  const status = error.response.status;
  const data = error.response.data || {};
  const detail = data.detail;

  // Known English detail → French
  if (typeof detail === 'string' && ERROR_DETAIL_FR[detail]) {
    data.detail = ERROR_DETAIL_FR[detail];
    error.response.data = data;
    return;
  }

  // Generic 5xx fallback when the backend didn't supply a usable French detail
  if (status >= 500 && status < 600) {
    if (typeof detail !== 'string' || !detail.trim()) {
      data.detail = 'Erreur serveur. Réessayez dans quelques instants.';
      error.response.data = data;
    }
  }
};

// Create axios instance with base URL and credentials support
const api = axios.create({
  baseURL: API_URL + '/api',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Enable httpOnly cookies
});

// Request interceptor - add auth token if available
api.interceptors.request.use(
  (config) => {
    markApiStart(_currentPath());
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor - handle auth errors
api.interceptors.response.use(
  (response) => {
    markApiEnd(_currentPath());
    return response;
  },
  async (error) => {
    markApiEnd(_currentPath());
    const originalRequest = error.config;
    
    // If 401 and not already retrying, try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      try {
        // Try to refresh the token
        const response = await axios.post(
          `${API_URL}/api/auth/refresh`,
          { refreshToken: localStorage.getItem('refreshToken') },
          { withCredentials: true }
        );

        if (response.data.token) {
          localStorage.setItem('token', response.data.token);
          if (response.data.refreshToken) {
            localStorage.setItem('refreshToken', response.data.refreshToken);
          }
          originalRequest.headers.Authorization = `Bearer ${response.data.token}`;
          return api(originalRequest);
        }
      } catch (refreshError) {
        // Refresh failed - clear auth state
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        localStorage.removeItem('refreshToken');
        // Optionally redirect to login
        // window.location.href = '/login';
      }
    }

    // Translate English FastAPI detail to French before propagating so all
    // screens render user-facing copy without each one needing its own map.
    translateErrorDetail(error);
    return Promise.reject(error);
  }
);

export default api;
